import AddTaskIcon from "@mui/icons-material/AddTask";
import { IconButton, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  IMPACTS_URL,
  TASKS_SCHEDULE_URL,
  TASKS_SCHEDULE_UPDATE_URL,
  USERS_URL,
} from "../../Constants/Constants";
import AxiosInstance from "../../services/AxiosInstance";
import { useAlert } from "../alerts/AlertContext";
import TaskSchedulerDetails from "./TaskSchedulerDetails";
import TaskSchedulerMenu from "./TaskSchedulerMenu";
import TaskSchedulerSchedule from "./TaskSchedulerSchedule";
import { generateCron } from "./taskSchedulerUtils";

function TaskSchedulerDialog({
  scheduleId,
  selectedScheduleName,
  fetchTaskSchdules,
}) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priorities, setPriorities] = useState([]);
  const [taskPriorityId, setTaskPriorityId] = useState(0);
  const [assignToId, setAssignToId] = useState("0");
  const [assignees, setAssignees] = useState([]);

  const [frequency, setFrequency] = useState("DAILY");
  const [weekDays, setWeekDays] = useState([]);
  const [monthDays, setMonthDays] = useState([]);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [monthOfYear, setMonthOfYear] = useState(1);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [time, setTime] = useState(new Date());
  const [active, setActive] = useState(true);

  const cronExpression = generateCron({
    frequency,
    time,
    weekDays,
    monthDays,
    dayOfMonth,
    monthOfYear,
  });

  const handleNameChange = (event) => {
    setName(event.target.value);
  };
  const handleTaskTitleChange = (event) => {
    setTaskTitle(event.target.value);
  };
  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };
  const handleTaskPriorityChange = (event) => {
    setTaskPriorityId(event.target.value);
  };

  const handleAssignToChange = (event) => {
    setAssignToId(event.target.value);
  };

  const handleStatusChange = (event) => {
    setActive(event.target.value);
  };

  const handleClickOpen = () => {
    if (scheduleId !== 0) {
      getTaskSchedule();
    } else {
      setOpen(true);
    }
  };

  const getTaskSchedule = async () => {
    await AxiosInstance.get(TASKS_SCHEDULE_URL + scheduleId)
      .then((response) => {
        const scheduleData = response.data;
        if (scheduleData !== null) {
          setName(scheduleData.name);
          setTaskPriorityId(scheduleData.priorityId);
          setAssignToId(scheduleData.assignId);
          setTaskTitle(scheduleData.title);
          setDescription(scheduleData.description);
          setStartDate(new Date(scheduleData.startDate));
          setEndDate(new Date(scheduleData.endDate));
          setActive(scheduleData.active);
          setFrequency(scheduleData.frequency);
          if (scheduleData.frequency === "WEEKLY") {
            setWeekDays(
              scheduleData.weekly.split(",").map((item) => item.trim())
            );
          }
          if (scheduleData.frequency === "MONTHLY") {
            setMonthDays(
              scheduleData.monthly.split(",").map((item) => item.trim())
            );
          }
          if (scheduleData.frequency === "YEARLY") {
            setDayOfMonth(scheduleData.dayOfMonth);
            setMonthOfYear(scheduleData.monthOfYear);
          }
          const newTimeString = scheduleData.runTime;
          const [hours, minutes] = newTimeString.split(":");
          const runTimeDate = new Date();
          runTimeDate.setUTCHours(hours);
          runTimeDate.setUTCMinutes(minutes);
          runTimeDate.setUTCSeconds(0);

          setTime(runTimeDate);
        }

        setOpen(true);
      })
      .catch((err) => {
        console.error(err);
        if (err.response?.status === 404) {
          setOpen(true);
        }
      });
  };

  const showAlert = useAlert();

  useEffect(() => {
    initializeSysValues();
  }, []);

  const initializeSysValues = () => {
    AxiosInstance.get(IMPACTS_URL).then((response) =>
      setPriorities(response.data)
    );

    AxiosInstance.get(USERS_URL).then((response) =>
      setAssignees(response.data.data)
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setLoading(true);

    try {
      const taskScheduleOjb = {};
      if (scheduleId !== 0) taskScheduleOjb.id = scheduleId;

      taskScheduleOjb.name = name;
      taskScheduleOjb.title = taskTitle;
      taskScheduleOjb.description = description;
      taskScheduleOjb.impact = { id: taskPriorityId };
      taskScheduleOjb.assign = { id: assignToId };
      taskScheduleOjb.frequency = frequency;

      //taskScheduleOjb.runTime = time.toTimeString().slice(0, 5);
      taskScheduleOjb.runTime = time.toISOString().slice(11, 16);
      taskScheduleOjb.cron = cronExpression;
      taskScheduleOjb.startDate = startDate;
      taskScheduleOjb.endDate = endDate;
      taskScheduleOjb.active = active;

      if (frequency === "WEEKLY") {
        taskScheduleOjb.weekly = weekDays.join(", ");
      }
      if (frequency === "MONTHLY") {
        taskScheduleOjb.monthly = monthDays.join(", ");
      }
      if (frequency === "YEARLY") {
        taskScheduleOjb.dayOfMonth = dayOfMonth;
        taskScheduleOjb.monthOfYear = monthOfYear;
      }

      await AxiosInstance.post(
        scheduleId !== 0 ? TASKS_SCHEDULE_UPDATE_URL : TASKS_SCHEDULE_URL,
        taskScheduleOjb
      );

      showAlert(
        `Task schedule ${scheduleId !== 0 ? "updated" : "created"} successfully!`,
        "success"
      );

      fetchTaskSchdules();
      handleClose();
    } catch (error) {
      console.error("Error saving Task schedule:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
    }
  };

  return (
    <>
      {scheduleId === 0 ? (
        <Tooltip
          title="Schedule Task"
          onClick={handleClickOpen}
          style={{ cursor: "pointer", margin: "2px", border:"0px" }}
        >
          <IconButton  >
            <AddTaskIcon color="primary" />
          </IconButton>
        </Tooltip>
      ) : (
        <Link onClick={handleClickOpen}>{selectedScheduleName}</Link>
      )}

      <TaskSchedulerMenu
        open={open}
        onClose={handleClose}
        loading={loading}
        scheduleId={scheduleId}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        detailsContent={
          <TaskSchedulerDetails
            name={name}
            taskTitle={taskTitle}
            description={description}
            taskPriorityId={taskPriorityId}
            assignToId={assignToId}
            priorities={priorities}
            assignees={assignees}
            onNameChange={handleNameChange}
            onTaskTitleChange={handleTaskTitleChange}
            onDescriptionChange={handleDescriptionChange}
            onTaskPriorityChange={handleTaskPriorityChange}
            onAssignToChange={handleAssignToChange}
          />
        }
        scheduleContent={
          <TaskSchedulerSchedule
            frequency={frequency}
            weekDays={weekDays}
            monthDays={monthDays}
            dayOfMonth={dayOfMonth}
            monthOfYear={monthOfYear}
            startDate={startDate}
            endDate={endDate}
            time={time}
            active={active}
            onFrequencyChange={(e) => setFrequency(e.target.value)}
            onWeekDaysChange={(e) => setWeekDays(e.target.value)}
            onMonthDaysChange={(e) => setMonthDays(e.target.value)}
            onDayOfMonthChange={(e) => setDayOfMonth(Number(e.target.value))}
            onMonthOfYearChange={(e) => setMonthOfYear(Number(e.target.value))}
            onStartDateChange={(newValue) => setStartDate(newValue)}
            onEndDateChange={(newValue) => setEndDate(newValue)}
            onTimeChange={(newTime) => setTime(newTime)}
            onActiveChange={handleStatusChange}
          />
        }
      />
    </>
  );
}

export default TaskSchedulerDialog;
